<template>
    <div height="medium" width="large">
        <div class="container">
            <h2 class="heading">{{ $t('modals.patient.tab.label_one') }}</h2>
            <div class="main">
                <div>
                    <h2 class="heading_title">
                        {{ $t('modals.patient.medical.heading') }}
                    </h2>
                    <p class="subtitle">{{ $t('modals.patient.medical.subtitle') }}</p>
                </div>
                <div>
                    <button class="export-btn" @click="openExportTimelineModal">
                        <common-icon name="file" size="semi-small"></common-icon>
                        <span>{{ $t('modals.patient.medical.title') }}</span>
                    </button>
                </div>
            </div>
            <div class="content-section">
                <h4 class="head">
                    <common-icon name="current" size="medium"></common-icon>
                    {{ $t('modals.patient.medical.head') }}
                </h4>
                <div class="cards">
                    <div class="cardes blue-card">
                        <h3 class="titles">
                            <common-icon name="user" size="medium"></common-icon>
                            {{ $t('modals.patient.medical.blue_card.card_head') }}
                        </h3>
                        <p class="periodic">{{ activeConditionInfo?.subtitle }}</p>
                        <p class="result">{{ activeConditionInfo?.detail }}</p>
                    </div>
                    <div class="cardes green-card">
                        <h3 class="titles">
                            <common-icon name="file" size="medium"></common-icon>
                            {{ $t('modals.patient.medical.green_card.card_head') }}
                        </h3>
                        <p class="periodic">{{ recentTestInfo?.subtitle }}</p>
                        <p class="result">{{ recentTestInfo?.detail }}</p>
                    </div>
                    <div class="cardes purple-card">
                        <h3 class="titles">
                            <common-icon name="timeline" size="medium"></common-icon>
                            {{ $t('modals.patient.medical.purple_card.card_head') }}
                        </h3>
                        <p class="periodic">{{ nextAppointmentInfo?.subtitle }}</p>
                        <p class="result">{{ nextAppointmentInfo?.detail }}</p>
                    </div>
                </div>
            </div>
            <div class="content-section">
                <h4 class="head">
                    <common-icon name="timer" size="medium"></common-icon>
                    {{ $t('modals.patient.medical.time_head') }}
                </h4>
                <div class="timeline-wrap">
                    <template v-if="isLoadingAppointments">
                        <div class="loading-spinner">
                            <span>{{ $t('modals.import_contacts_modal.loading') }}</span>
                            <loading-spin></loading-spin>
                        </div>
                    </template>

                    <div v-else-if="appointmentError || steps.length === 0" class="empty-state">
                        <common-icon name="emergancy" size="extra-huge"></common-icon>
                        <p class="empty-state-message">
                            {{ appointmentError || $t('modals.patient.medical.no_appointments') }}
                        </p>
                    </div>

                    <div v-else class="timeline-inner">
                        <div
                            v-for="(step, idx) in steps"
                            :key="step.id"
                            class="timeline-item"
                            :class="{ active: activeIndex === idx }"
                        >
                            <div class="marker-col">
                                <div class="marker">
                                    <span class="dot" :class="{ completed: idx < activeIndex }"></span>
                                    <span class="line"></span>
                                </div>
                            </div>

                            <div class="card-col">
                                <article class="card-layout" @click="setActive(idx)">
                                    <header class="card-head">
                                        <div class="card-head-left">
                                            <div class="meta-date">
                                                <div class="date">
                                                    <common-icon name="timeline" size="semi-small"></common-icon>
                                                    <p>{{ getStepDate(step) }}</p>
                                                </div>
                                                <div class="time">
                                                    <common-icon name="timer" size="semi-small"></common-icon>
                                                    <p>{{ getStepTime(step) }}</p>
                                                </div>
                                            </div>
                                            <div class="card-title-wrap">
                                                <h3 class="card-title">{{ getStepTitle(step) }}</h3>
                                                <div class="tags">
                                                    <span class="tag" :class="getStepTagColor(step)">
                                                        {{ getStepTag(step) }}
                                                    </span>
                                                    <span class="tag">
                                                        {{ getStepSpeciality(step?.referral?.fromUser) }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="card_grid">
                                            <div class="user-provider">
                                                <common-icon name="user" size="semi-small"></common-icon>
                                                {{ getStepProvider(step) }}
                                            </div>
                                            <div class="location">
                                                <common-icon name="location" size="semi-small"></common-icon>
                                                {{ getStepLocation(step) }}
                                            </div>
                                        </div>
                                    </header>

                                    <div class="card-body">
                                        <section class="section diagnosis">
                                            <div class="section-title">
                                                {{ $t('modals.patient.medical.steps.diagnosis_name') }}
                                            </div>
                                            <div class="section-content">
                                                {{ step.referral?.diagnosis }}
                                            </div>
                                        </section>

                                        <div class="actions">
                                            <button class="btn" @click.stop="openViewDetailsModal(step.referral)">
                                                <common-icon name="file" size="semi-small"></common-icon>
                                                {{ $t('modals.patient.medical.steps.view') }}
                                            </button>
                                            <button class="btn" @click.stop="openContactProviderModal(step.referral)">
                                                <common-icon name="sms" size="semi-small"></common-icon>
                                                {{ $t('modals.patient.medical.steps.contect') }}
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            </div>
                        </div>
                    </div>
                    <div v-if="hasMoreAppointments && !isLoadingAppointments" class="load">
                        <button class="load-button" :disabled="isLoadingAppointments" @click="$emit('load-more')">
                            {{ $t('modals.patient.medical.steps.load') }}
                            <common-icon name="leftarrow" size="tiny"></common-icon>
                        </button>
                    </div>
                </div>
            </div>

            <medical-export-timeline-modal
                :isOpen="isExportTimelineModalOpen"
                :appointments="appointments"
                @close="closeExportTimelineModal"
            ></medical-export-timeline-modal>

            <medical-view-details-modal
                :isOpen="showViewDetailsModal"
                :selectedReferral="selectedReferral"
                @close="closeViewDetailsModal"
            ></medical-view-details-modal>

            <medical-contact-provider-modal
                :isOpen="showContactModal"
                :selectedReferral="selectedReferral"
                @close="closeContactModal"
            ></medical-contact-provider-modal>
        </div>
    </div>
</template>

<script>
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import ReferralTrackingTypeEnum from '@/enums/ReferralTrackingTypeEnum'
    import ReferralStatusEnum from '@/enums/ReferralStatusEnum'
    import { Circle9 } from 'vue-loading-spinner'
    import MedicalViewDetailsModal from './MedicalViewDetailsModal.vue'
    import MedicalContactProviderModal from './MedicalContactProviderModal.vue'
    import MedicalExportTimelineModal from './MedicalExportTimelineModal.vue'

    export default {
        mixins: [PageWithDataMixin],
        components: {
            'loading-spin': Circle9,
            MedicalViewDetailsModal,
            MedicalContactProviderModal,
            MedicalExportTimelineModal,
        },
        props: {
            appointments: {
                type: Array,
                default: () => [],
            },
            isLoadingAppointments: {
                type: Boolean,
                default: false,
            },
            appointmentError: {
                type: String,
                default: '',
            },
            hasMoreAppointments: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                activeIndex: 0,
                showViewDetailsModal: false,
                showContactModal: false,
                isExportTimelineModalOpen: false,
                selectedReferral: null,
            }
        },
        computed: {
            steps() {
                return (this.appointments || []).map((referral) => ({ id: referral.id, referral }))
            },
            activeConditionInfo() {
                const itemWithDiagnosis = (this.appointments || []).find((item) => item && item.diagnosis)
                if (!itemWithDiagnosis) {
                    return {
                        subtitle: this.$t('modals.patient.medical.blue_card.card_subtitle'),
                        detail: this.$t('modals.patient.medical.blue_card.card_detail'),
                    }
                }
                const totalDiagnoses = new Set((this.appointments || []).map((item) => item && item.diagnosis).filter(Boolean))
                    .size

                return {
                    subtitle: itemWithDiagnosis.diagnosis,
                    detail:
                        totalDiagnoses > 1
                            ? this.$t('modals.patient.medical.blue_card.active_conditions', { count: totalDiagnoses })
                            : this.$t('modals.patient.medical.blue_card.under_monitoring'),
                }
            },
            recentTestInfo() {
                let allTests = []
                for (const item of this.appointments || []) {
                    if (item && Array.isArray(item.facilityServiceTests) && item.facilityServiceTests.length > 0) {
                        allTests = allTests.concat(item.facilityServiceTests)
                    }
                }

                if (allTests.length === 0) {
                    return {
                        subtitle: this.$t('modals.patient.medical.green_card.card_subtitle'),
                        detail: this.$t('modals.patient.medical.green_card.card_detail'),
                    }
                }

                const firstTest = allTests[0]
                const groupName = firstTest.group?.name || ''
                let detailText = ''
                if (groupName) {
                    const translationKey =
                        allTests.length > 1
                            ? 'modals.patient.medical.green_card.tests_with_group'
                            : 'modals.patient.medical.green_card.single_test_with_group'
                    detailText = this.$t(translationKey, { count: allTests.length, group: groupName })
                } else {
                    detailText = this.$t('modals.patient.medical.green_card.tests_recorded', { count: allTests.length })
                }

                return {
                    subtitle: firstTest.name || this.$t('modals.patient.medical.green_card.recent_test_completed'),
                    detail: detailText,
                }
            },
            nextAppointmentInfo() {
                const now = new Date()
                const scheduledApps = (this.appointments || [])
                    .filter((item) => item && item.appointmentDateTime)
                    .sort((a, b) => new Date(a.appointmentDateTime) - new Date(b.appointmentDateTime))

                const futureApp = scheduledApps.find((item) => new Date(item.appointmentDateTime) >= now)
                const targetApp = futureApp || scheduledApps[0]

                if (!targetApp) {
                    return {
                        subtitle: this.$t('modals.patient.medical.purple_card.card_subtitle'),
                        detail: this.$t('modals.patient.medical.purple_card.card_detail'),
                    }
                }

                const providerName = targetApp.fromUser
                    ? [targetApp.fromUser.firstName, targetApp.fromUser.lastName].filter(Boolean).join(' ')
                    : ''

                return {
                    subtitle: providerName
                        ? this.$t('modals.patient.medical.purple_card.appointment_with', { name: providerName })
                        : targetApp.diagnosis || this.$t('modals.patient.medical.purple_card.follow_up_scheduled'),
                    detail: this.formatDate(targetApp.appointmentDateTime),
                }
            },
        },
        methods: {
            setActive(i) {
                this.activeIndex = i
            },
            openExportTimelineModal() {
                this.isExportTimelineModalOpen = true
            },
            closeExportTimelineModal() {
                this.isExportTimelineModalOpen = false
            },
            openViewDetailsModal(referral) {
                if (!referral) return
                this.selectedReferral = referral
                this.showViewDetailsModal = true
            },
            closeViewDetailsModal() {
                this.showViewDetailsModal = false
                this.selectedReferral = null
            },
            openContactProviderModal(referral) {
                if (!referral) return
                this.selectedReferral = referral
                this.showContactModal = true
            },
            closeContactModal() {
                this.showContactModal = false
                this.selectedReferral = null
            },
            getProviderFullName(user) {
                if (!user) return ''
                const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ')
                const designation = user.designation || ''
                return [fullName, designation].filter(Boolean).join(', ')
            },
            getStepDate(step) {
                return this.formatDate(step.referral?.appointmentDateTime || step.referral?.createdDateTime)
            },
            getStepTime(step) {
                return this.formatDate(step.referral?.appointmentDateTime || step.referral?.createdDateTime, true)
            },
            getStepSpeciality(user) {
                if (!user || !user.specialities || user.specialities.length === 0) {
                    return ''
                }

                return user.specialities[0]?.speciality?.name || ''
            },
            getStepTitle(step) {
                if (step?.referral?.followUpReferral) {
                    return this.$t('modals.patient.medical.steps.followUp')
                }
                return `${this.getStepType(step?.referral?.sourceType)} ${this.$t('modals.patient.medical.steps.referral')}`
            },
            getStepType(type) {
                if (type === ReferralTrackingTypeEnum.DIRECT) return this.$t('pages.referrals.index.referrals_made.type_direct')
                if (type === ReferralTrackingTypeEnum.MATCH) return this.$t('pages.referrals.index.referrals_made.type_matched')
                return 'N/A'
            },
            getStepTag(step) {
                const displayStatus = this.getDisplayStatus(step?.referral?.status, step?.referral?.sourceType)

                return this.$t(`pages.referrals.tracking.status_title.${displayStatus}`)
            },
            getDisplayStatus(status, sourceType) {
                const statusMapping = {
                    [ReferralTrackingTypeEnum.MATCH]: {
                        [ReferralStatusEnum.CREATED]: 'CREATED',
                        [ReferralStatusEnum.TIME_SLOTS_PROPOSED]: 'AWAITING_CONFIRMATION',
                        [ReferralStatusEnum.DECLINED]: 'DECLINED',
                        [ReferralStatusEnum.SCHEDULED]: 'SCHEDULED',
                        [ReferralStatusEnum.INACTIVE]: 'EXPIRED',
                    },

                    [ReferralTrackingTypeEnum.DIRECT]: {
                        [ReferralStatusEnum.PATIENT_VISIT_PENDING]: 'SCHEDULED',
                        [ReferralStatusEnum.PATIENT_VISIT_PAST]: 'NO_SHOW',
                        [ReferralStatusEnum.NO_SHOW]: 'NO_SHOW',
                        [ReferralStatusEnum.CANCELLED]: 'CANCELLED',
                        [ReferralStatusEnum.CONSULTATION_NOTES_PENDING]: 'CONSULTATION_NOTES_PENDING',
                        [ReferralStatusEnum.COMPLETED]: 'COMPLETED',
                    },
                }

                return statusMapping[sourceType]?.[status] || status
            },
            getStepTagColor(step) {
                const displayStatus = this.getDisplayStatus(step?.referral?.status, step?.referral?.sourceType)

                const yellowStatuses = ['CREATED', 'AWAITING_CONFIRMATION']
                const blueStatuses = ['SCHEDULED', 'COMPLETED']
                const redStatuses = ['DECLINED', 'CANCELLED', 'NO_SHOW', 'EXPIRED']
                const greenStatuses = ['CONSULTATION_NOTES_PENDING']

                if (yellowStatuses.includes(displayStatus)) return 'yellow'
                if (blueStatuses.includes(displayStatus)) return 'blue'
                if (redStatuses.includes(displayStatus)) return 'today'
                if (greenStatuses.includes(displayStatus)) return 'green'

                return 'gray'
            },
            getStepProvider(step) {
                const provider = step.referral?.toUser || step.referral?.fromUser
                if (!provider) return ''
                const fullName = [provider.firstName, provider.lastName].filter(Boolean).join(' ')
                const designation = provider.designation || ''
                return [fullName, designation].filter(Boolean).join(', ')
            },
            getStepLocation(step) {
                const location = step.referral?.practiceLocation
                return location && [location.name, location.address, location.state, location.zip].filter(Boolean).join(', ')
                    ? [location.name, location.address, location.state, location.zip].filter(Boolean).join(', ')
                    : ''
            },
            formatDate(value, includeTime = false) {
                if (!value) return ''
                const date = new Date(value)
                if (Number.isNaN(date.getTime())) return ''
                return includeTime
                    ? date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                    : date.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' })
            },
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/components/patients/_patient';

    .heading {
        font-size: 16px;
        color: oklch(0.21 0.034 264.665);
        font-weight: 500;
        margin-bottom: 14px;
    }
    .heading_title {
        font-size: 18px;
        color: oklch(0.21 0.034 264.665);
        font-weight: 500;
    }
    .subtitle {
        color: #666;
        margin-bottom: 16px;
        font-size: 12px;
        font-weight: 400;
    }
    .main {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
    .export-btn {
        background-color: #fff;
        color: #0a0a0a;
        font-weight: 500;
        border: 1px solid #ccc;
        border-radius: 6.75px;
        padding: 6px 8.75px;
        cursor: pointer;
        font-size: 12.25px;
        display: inline-flex;
        align-items: center;
        gap: 10px;
        &:hover {
            background-color: #ddd;
            transition: background-color 0.15s ease;
        }
    }
    .load {
        display: flex;
        justify-content: center;
        color: #030213;
        align-items: center;
    }
    .load p:hover {
        text-decoration: underline;
    }
    .load p {
        font-size: 14px;
        color: #030213;
        font-weight: 400;
        cursor: pointer;
        margin-right: 12px;
    }
    .load-button {
        border: 0;
        background: transparent;
        color: #030213;
        font-size: 14px;
        font-weight: 400;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .load-button:disabled {
        cursor: wait;
        opacity: 0.6;
    }
    .load-status,
    .error-message {
        text-align: center;
        font-size: 12px;
    }
    .error-message {
        color: #b91c1c;
    }
    .content-section {
        padding: 15px;
        border-radius: 12px;
        border: 1px solid #ddd;
        margin-bottom: 20px;
        background-color: #fff;
    }
    .user-provider {
        margin-bottom: 6px;
        color: oklch(0.145 0 0);
        font-size: 12px;
        font-weight: 400;
    }
    .location {
        font-size: 12px;
    }
    .cards {
        display: grid;
        grid-template-columns: 1fr;
        gap: 16px;
        border: none;
    }
    .head {
        font-size: 14px;
        font-weight: 400;
        color: oklch(0.145 0 0);
        padding: 8px 1px;
    }
    h4.head i {
        margin-right: 7px;
    }
    @media (min-width: 768px) {
        .cards {
            grid-template-columns: repeat(3, 1fr);
        }
    }
    .cardes {
        padding: 16px;
        border-radius: 8px;
    }
    .cardes h3 {
        font-weight: 500;
    }
    .blue-card {
        background-color: oklch(0.97 0.014 254.604);
        color: #1e40af;
    }
    .blue-card i {
        color: oklch(0.546 0.245 262.881);
    }
    .green-card {
        background-color: oklch(0.982 0.018 155.826);
        color: #065f46;
    }
    .green-card i {
        color: oklch(0.627 0.194 149.214);
    }
    .purple-card {
        background-color: oklch(0.977 0.014 308.299);
        color: #5b21b6;
    }
    .purple-card i {
        color: oklch(0.558 0.288 302.321);
    }
    .timeline-wrap {
        padding: 20px 0px;
        min-height: 100vh;
        font-family: system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial;
        color: #1f2937;
    }
    .timeline-item {
        display: flex;
        align-items: stretch;
        gap: 16px;
        margin-bottom: 20px;
    }
    .marker-col {
        width: 14px;
        display: flex;
        justify-content: center;
        position: relative;
    }
    .marker {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        height: 100%;
    }
    .dot {
        width: 12px;
        height: 12px;
        flex-shrink: 0;
        border-radius: 50%;
        background: #2b7fff;
        display: inline-block;
        box-shadow: 0 0 0 4px rgba(96, 165, 250, 0.12);
        transition: background 0.2s, transform 0.15s;
    }
    .dot.completed {
        background: #2563eb;
        box-shadow: 0 0 0 6px rgba(37, 99, 235, 0.08);
        transform: scale(1.05);
    }
    .line {
        flex: 1;
        width: 4px;
        background: #2b7fff;
        margin-top: 6px;
        min-height: 0;
        display: block;
        border-radius: 1px;
    }
    .card-col {
        flex: 1;
    }
    .card-layout {
        border: 1px solid #e6eaf0;
        border-radius: 10px;
        padding: 14px;
        cursor: pointer;
        transition: transform 0.12s, box-shadow 0.12s;
    }
    .card-head-left {
        gap: 12px;
        align-items: flex-start;
    }
    .meta-date {
        min-width: 120px;
        color: #374151;
        display: flex;
        align-items: center;
        margin-bottom: 6px;
    }
    .date {
        font-size: 16px;
        font-weight: 500;
        color: oklch(0.145 0 0);
        align-items: center;
    }
    .date p {
        margin-left: 10px;
    }
    .time {
        font-size: 0.875rem;
        color: #6b7280;
        margin-left: 14px;
        font-weight: 400;
        display: flex;
        align-items: center;
    }
    .time p {
        margin-left: 10px;
    }
    .card-title-wrap {
        display: flex;
        flex-direction: column;
    }
    .card-title {
        font-weight: 500;
        color: #0a0a0a;
    }
    .tags {
        margin-top: 6px;
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
    }
    .tag {
        display: inline-flex;
        align-items: center;
        border: 1px solid #ddd;
        padding: 1.75px 7px;
        border-radius: 6.75px;
        font-size: 12px;
        margin-right: 5px;
        gap: 10px;
        font-weight: 500;
        color: #0a0a0a;
    }
    .tag.blue {
        color: #2563eb;
        background-color: #e6f0ff;
        border-color: #93c5fd;
    }
    .tag.yellow {
        background-color: #fefce8;
        color: #a65f00;
        border-color: #fde047;
    }
    .tag.today {
        background-color: #fef2f2;
        color: #c10007;
        border-color: #ffc9c9;
    }
    .tag.green {
        background-color: #f0fdf4;
        color: #008236;
        border-color: #b9f8cf;
    }
    .tag.gray {
        background-color: #f3f4f6;
        color: #364153;
        border-color: #e5e7eb;
    }
    .card_grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
        margin-top: 12px;
    }
    .card-body {
        margin-top: 14px;
        display: flex;
        flex-direction: column;
        gap: 12px;
    }
    .section {
        background: oklch(0.985 0.002 247.839);
        padding: 10px;
        border-radius: 8px;
    }
    .section-title {
        font-weight: 600;
        margin-bottom: 3px;
        color: #111827;
    }
    .section-content {
        color: #0a0a0a;
        line-height: 1.4;
        font-size: 12.25px;
        font-weight: 400;
    }
    .btn {
        border: 1px solid #ccc;
        border-radius: 6.75px;
        color: #0a0a0a;
        padding: 6px 8.75px;
        cursor: pointer;
        font-weight: 500;
        font-size: 12.25px;
        display: inline-flex;
        align-items: center;
        gap: 10px;
        &:hover {
            background-color: #ddd;
            transition: background-color 0.15s ease;
        }
    }
    .actions {
        display: flex;
        gap: 10px;
        margin-top: 10px;
        padding-top: 10px;
        border-top: 1px solid #e6eaf0;
    }

    @media (max-width: 720px) {
        .meta-date {
            min-width: 92px;
        }
    }
    @media screen and (max-width: 600px) {
        .actions,
        .main,
        .card_grid {
            display: block !important;
            margin-bottom: 10px !important;
        }
        .actions button {
            margin-bottom: 10px !important;
            width: 100% !important;
        }
    }
    .result {
        color: oklch(0.446 0.03 256.802);
        font-size: 12px;
    }
    p.periodic {
        font-size: 12px;
        color: oklch(0.145 0 0);
        font-weight: 400;
    }

    .view-details-modal {
        padding: 4px 0;
    }
    .details-header {
        margin-bottom: 16px;
        padding-bottom: 12px;
        border-bottom: 1px solid #e5e7eb;
    }
    .details-title-row {
        display: flex;
        align-items: center;
        gap: 10px;
    }
    .details-type {
        font-size: 15px;
        font-weight: 600;
        color: #111827;
    }
    .details-body {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }
    .details-group {
        background: #f9fafb;
        border-radius: 8px;
        padding: 12px;
        border: 1px solid #f3f4f6;
    }
    .group-title {
        font-size: 12px;
        font-weight: 600;
        color: #374151;
        margin-bottom: 8px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
    .group-content {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
    .detail-item {
        display: flex;
        justify-content: space-between;
        font-size: 13px;
    }
    .detail-label {
        color: #6b7280;
        font-weight: 500;
    }
    .detail-value {
        color: #111827;
        font-weight: 500;
        text-align: right;
    }

    .contact-provider-modal {
        padding: 4px 0;
    }
    .provider-info-card {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 12px;
        background: #f8fafc;
        border-radius: 10px;
        margin-bottom: 16px;
        border: 1px solid #e2e8f0;
    }
    .provider-avatar {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        overflow: hidden;
        background: #e2e8f0;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }
    .avatar-img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
    .provider-details {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }
    .provider-name {
        font-size: 15px;
        font-weight: 600;
        color: #0f172a;
    }
    .provider-speciality {
        font-size: 13px;
        color: #2563eb;
        font-weight: 500;
    }
    .provider-practice {
        font-size: 12px;
        color: #64748b;
    }
    .contact-actions-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }
    .contact-action-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 14px;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        background: #ffffff;
    }
    .contact-meta {
        display: flex;
        align-items: center;
        gap: 10px;
    }
    .contact-text {
        display: flex;
        flex-direction: column;
    }
    .contact-label {
        font-size: 11px;
        text-transform: uppercase;
        color: #6b7280;
        font-weight: 600;
    }
    .contact-value {
        font-size: 13px;
        color: #111827;
        font-weight: 500;
    }
    .contact-action-btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 6px 12px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 500;
        text-decoration: none;
        transition: background-color 0.15s ease;
    }
    .contact-action-btn.email-btn {
        background-color: #eff6ff;
        color: #2563eb;
        border: 1px solid #bfdbfe;
        &:hover {
            background-color: #dbeafe;
        }
    }
    .contact-action-btn.phone-btn {
        background-color: #f0fdf4;
        color: #16a34a;
        border: 1px solid #bbf7d0;
        &:hover {
            background-color: #dcfce7;
        }
    }

    .loading-spinner {
        text-align: center;
        font-size: 16px;

        .spinner {
            margin: 0 auto;
            zoom: 150%;
        }
    }

    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 40px 20px;
        text-align: center;
        background: #f9fafb;
        border: 1px dashed #d1d5db;
        border-radius: 12px;
        margin: 16px 0;
        gap: 10px;
    }
    .empty-state-message {
        font-size: 14px;
        color: #6b7280;
        font-weight: 500;
        margin: 0;
    }
</style>
